import React, { useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  ListSubheader,
  Box,
  Avatar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import useStore from '../Store/Store';
import boyAvatar from '../images/boy.png';
import girlAvatar from '../images/girl.png';

const SearchableSelect = ({
  label,
  options = [],
  value,
  onChange,
  fullWidth = true,
  style,
  renderOption,
  getOptionValue,
  getOptionLabel,
  searchFields = ['label'],
  placeholder = 'Search...',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const user = useStore((state) => state.user);

  const normalize = (str = '') => str.toLowerCase().trim().replace(/\s+/g, ' ');
  // Create "My self" option - always show it if user exists
  const myselfOption = {
    value: 'myself',
    label: 'My self',
    isMyself: true,
    userInfo: {
      firstName: 'My Self',
      lastName: '',
      imageUrl: user?.userInfo?.imageUrl || '',
      gender: user?.userInfo?.gender || '',
    },
  };

  const allOptions = [myselfOption, ...options];

  const filteredOptions = !searchTerm.trim()
    ? allOptions
    : allOptions.filter((option) => {
        if (option.isMyself) return true;

        const query = normalize(searchTerm);

        // Build fullName for combined searching
        const firstName = option?.userInfo?.firstName || '';
        const lastName = option?.userInfo?.lastName || '';
        const fullName = normalize(`${firstName} ${lastName}`);

        return (
          // Check fullName first
          fullName.includes(query) ||
          // Then check individual searchFields
          searchFields.some((field) => {
            const fieldValue = field.split('.').reduce((obj, key) => obj?.[key], option);
            return normalize(fieldValue?.toString()).includes(query);
          })
        );
      });

  const handleChange = (event) => {
    onChange(event);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm(''); // Clear search when closing
  };

  const getValue = (option) => {
    if (getOptionValue) return getOptionValue(option);
    return typeof option === 'object' ? option.value || option.id || option._id : option;
  };

  const getLabel = (option) => {
    if (getOptionLabel) return getOptionLabel(option);
    // Always return "My self" for myself option, regardless of user data
    if (option.isMyself) return 'My self';
    return typeof option === 'object'
      ? option.label ||
          option.name ||
          option.fullName ||
          `${option.userInfo?.firstName || ''} ${option.userInfo?.lastName || ''}`.trim()
      : option;
  };

  // Default render option function to match profile style
  const defaultRenderOption = (option) => {
    const getDefaultAvatar = () => {
      if (option?.userInfo?.gender === 'female') {
        return girlAvatar;
      } else if (option?.userInfo?.gender === 'male') {
        return boyAvatar;
      }
      return boyAvatar; // default fallback
    };

    // Special rendering for "My self" option
    if (option.isMyself) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            padding: '4px 0',
          }}
        >
          <Avatar
            src={option?.userInfo?.imageUrl || getDefaultAvatar()}
            sx={{
              width: '32px',
              height: '32px',
              // bgcolor: '#e3f2fd',
              // border: '2px solid #2196f3',
            }}
          >
            {!option?.userInfo?.imageUrl && <PersonIcon sx={{ color: '#2196f3' }} />}
          </Avatar>
          <span
            style={{
              fontFamily: 'Roboto, var(--default-font-family)',
              fontSize: '14px',
              color: '#1d1d1f',
              fontWeight: '500',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            My self
          </span>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          width: '100%',
        }}
      >
        <Avatar
          src={option?.userInfo?.imageUrl || getDefaultAvatar()}
          sx={{
            width: '32px',
            height: '32px',
          }}
        >
          {!option?.userInfo?.imageUrl && <PersonIcon />}
        </Avatar>
        <span
          style={{
            fontFamily: 'Roboto, var(--default-font-family)',
            fontSize: '14px',
            color: '#1d1d1f',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {getLabel(option)}
        </span>
      </Box>
    );
  };

  return (
    <FormControl fullWidth={fullWidth} style={style}>
      <InputLabel
        id={`searchable-select-label-${label}`}
        sx={{
          px: 1,
          background: '#fff',
          fontFamily: 'Roboto, var(--default-font-family)',
          fontWeight: '500',
        }}
      >
        {label}
      </InputLabel>
      <Select
        labelId={`searchable-select-label-${label}`}
        id={`searchable-select-${label}`}
        value={value || ''}
        label={label}
        onChange={handleChange}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        renderValue={(selected) => {
          if (!selected) return '';
          const selectedOption = allOptions.find((option) => getValue(option) === selected);
          if (!selectedOption) return selected;

          const getDefaultAvatar = () => {
            if (selectedOption?.userInfo?.gender === 'female') {
              return girlAvatar;
            } else if (selectedOption?.userInfo?.gender === 'male') {
              return boyAvatar;
            }
            return boyAvatar; // default fallback
          };

          // Special rendering for "My self" option
          if (selectedOption.isMyself) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Avatar
                  src={selectedOption?.userInfo?.imageUrl || getDefaultAvatar()}
                  sx={{
                    width: '24px',
                    height: '24px',
                    // bgcolor: '#e3f2fd',
                    // border: '2px solid #2196f3',
                  }}
                >
                  {!selectedOption?.userInfo?.imageUrl && <PersonIcon sx={{ color: '#2196f3', fontSize: '16px' }} />}
                </Avatar>
                <span
                  style={{
                    fontFamily: 'Roboto, var(--default-font-family)',
                    fontSize: '14px',
                    color: '#1d1d1f',
                    fontWeight: '500',
                  }}
                >
                  My self
                </span>
              </Box>
            );
          }

          // Regular option rendering
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Avatar
                src={selectedOption?.userInfo?.imageUrl || getDefaultAvatar()}
                sx={{
                  width: '24px',
                  height: '24px',
                }}
              >
                {!selectedOption?.userInfo?.imageUrl && <PersonIcon sx={{ fontSize: '16px' }} />}
              </Avatar>
              <span
                style={{
                  fontFamily: 'Roboto, var(--default-font-family)',
                  fontSize: '14px',
                  color: '#1d1d1f',
                }}
              >
                {getLabel(selectedOption)}
              </span>
            </Box>
          );
        }}
        sx={{
          borderRadius: '100px',
          backgroundColor: '#ffffff',
          fontFamily: 'Roboto, var(--default-font-family)',
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: 'none',
            boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.1)',
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: '12px',
              marginTop: '8px',
              boxShadow: '0px 2px 8px rgba(0,0,0,0.32)',
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
      >
        {/* Search input inside dropdown using ListSubheader */}
        <ListSubheader
          sx={{
            backgroundColor: 'transparent',
            p: 1,
            lineHeight: 1,
          }}
        >
          <TextField
            placeholder={placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            fullWidth
            variant="outlined"
            autoFocus
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '100px',
                backgroundColor: '#f5f5f5',
                '& fieldset': {
                  border: 'none',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#666' }} />
                </InputAdornment>
              ),
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </ListSubheader>

        {/* Options */}
        {filteredOptions.map((option, index) => (
          <MenuItem
            key={getValue(option) || index}
            value={getValue(option)}
            sx={{
              padding: '12px 16px',
              minHeight: 'auto',
              '&:hover': { backgroundColor: option.isMyself ? '#e3f2fd' : '#f0f0f0' },
              fontFamily: 'Roboto, var(--default-font-family)',
              borderBottom: option.isMyself && index < filteredOptions.length - 1 ? '1px solid #e0e0e0' : 'none',
              marginBottom: option.isMyself ? '4px' : '0',
            }}
          >
            {renderOption ? renderOption(option) : defaultRenderOption(option)}
          </MenuItem>
        ))}

        {filteredOptions.length === 0 && searchTerm && (
          <MenuItem
            disabled
            sx={{
              padding: '12px 16px',
              textAlign: 'center',
              color: '#666',
              fontStyle: 'italic',
              fontFamily: 'Roboto, var(--default-font-family)',
            }}
          >
            No results found
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

export default SearchableSelect;
